// frontend/src/auth/inviteKey.js
//
// URL-BASED BETA INVITES  —  https://site.com/signup#invite=MYTHICFOUNDER
//
// This is what the printed QR code / business card resolves to. The invite value
// is a `BetaKey.key_code` in whatever format an admin created it — there is NO
// required prefix (see INVITE_KEY_PATTERN below for why assuming one broke the
// QR code entirely).
//
// WHY A FRAGMENT AND NOT `?beta_key=...`:
// the fragment is never sent to the server. It stays out of nginx/Django access
// logs, out of the `Referer` header on every outbound link, and out of any
// analytics pipeline that records `location.search`. A query string would leak a
// live, multi-use shared key into all three. Do not "simplify" this to a query
// param.
//
// WHY sessionStorage AND NOT REACT STATE:
// the fragment must survive a full page navigation away from the app and back.
// That includes a manual refresh AND a redirect-based sign-in: Clerk IS now wired
// up (see ClerkStatusBootstrap/ClerkBridgeCoordinator), and a Google OAuth round
// trip destroys all in-memory state. It is
// sessionStorage rather than localStorage deliberately: an invite is scoped to
// the tab the user opened the link in and MUST NOT silently attach itself to a
// different signup days later in the same browser.
//
// THE KEY IS NEVER AUTO-CONSUMED. Capture only prefills the field; the user
// still presses the button. Consumption is a `usage_count` increment on a
// limited, revocable shared key (`BetaKey.use_key()`), so a bot crawling an
// invite link must not be able to burn a seat by loading the page. This holds for
// the Clerk-native admission screen too (ClerkBetaAdmissionScreen prefills but
// never auto-submits).

/** Tab-scoped home of a captured invite, read back after any navigation. */
const INVITE_STORAGE_KEY = 'mythicInviteKey';

/**
 * Set when a link CONTAINED an invite that could not be parsed. Kept separate
 * from the key itself so the signup screen can say "that link looked wrong"
 * instead of rendering an empty box that silently pretends no link was used.
 */
const INVITE_ERROR_KEY = 'mythicInviteError';

/**
 * SHAPE VALIDATION ONLY — this is deliberately NOT the security boundary.
 *
 * ============================================================================
 * WHY THE OLD `GRUP_`-ONLY PATTERN WAS REMOVED (2026-09-13)
 * ============================================================================
 *
 * This used to be `/^GRUP_[A-Z0-9][A-Z0-9_-]{2,58}$/`, on the theory that the
 * `GRUP_` prefix proved a key was `SHARED` and therefore safe to distribute by
 * URL/QR. That theory was wrong in practice and broke the feature outright:
 *
 * In the Django model, `key_code` and `key_type` are INDEPENDENT fields. A
 * perfectly valid shared event key can have any human-readable code — the live
 * keys are `MYTHICFOUNDER` and friends, with no prefix at all. The prefix is an
 * admin-editable naming convention, not a type.
 *
 * So every real QR scan failed this regex, the key was discarded at capture,
 * and the user landed on the sign-up screen with an EMPTY beta-key box and a
 * "that invite link didn't look right" notice — asking them to type a key that
 * is printed nowhere on the card they just scanned. A dead end, for every
 * single scanner.
 *
 * ============================================================================
 * WHERE THE SECURITY BOUNDARY ACTUALLY LIVES
 * ============================================================================
 *
 * Removing the prefix filter does NOT relax the shared-only rule for URL
 * invites. That rule is enforced where it can actually be trusted: against the
 * backend validation response's real `key_type` field, in `BetaKeyGate.js`
 * (Clerk lane) and `BetaKeyValidation.js` (legacy lane). Both refuse to
 * auto-use a URL-borne key unless `key_type === 'SHARED'`.
 *
 * That check matters because an `INDIVIDUAL` key is a bearer credential bound
 * to one person's `target_email`, and that binding is NOT enforced at
 * redemption on the legacy path: `UserRegistrationSerializer.create()` re-runs
 * `BetaKey.is_valid()` (active/limit/expiry) but never calls
 * `can_be_used_by_email()`. So a leaked individual link must not be auto-usable
 * by whoever opened it — and `key_type` is the only field that can tell us,
 * since a prefix can be hand-edited to say anything.
 *
 * ============================================================================
 * WHAT THIS PATTERN IS FOR NOW
 * ============================================================================
 *
 * Rejecting input that could not possibly be a key, so we do not store garbage
 * or fire a doomed validation request. `BetaKey.key_code` is `max_length=100`,
 * so anything longer cannot exist in the database. Character set is kept
 * conservative (alphanumerics plus `_` and `-`) to exclude URL/markup junk from
 * a mangled link, while accepting every key-code format actually in use.
 *
 * Deliberately WEAK. The backend is the source of truth; a key that passes this
 * has proven nothing except that it is worth asking the server about.
 */
const INVITE_KEY_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,99}$/;

/**
 * Strip the fragment from the address bar without adding a history entry.
 *
 * `replaceState` (not `pushState`) so the Back button cannot walk the user
 * "back" onto the URL that still carries the key, and not `location.hash = ''`
 * which leaves a bare `#` behind and reloads nothing. `search` is preserved —
 * unrelated tracking/UTM params on the invite link are not ours to discard.
 */
function stripFragmentFromVisibleUrl() {
    const { pathname, search } = window.location;
    window.history.replaceState(null, '', `${pathname}${search}`);
}

/**
 * Read `#invite=...` once, at app start, and move it into tab storage.
 *
 * Returns the captured key, or `null` when the URL carried no invite at all.
 *
 * ABSENCE IS NOT FAILURE, AND ABSENCE MUST NOT CLEAR: the overwhelmingly common
 * case is a normal visit with no fragment, and the second most common is a
 * RETURN trip (refresh, or an auth provider redirecting back) where the
 * fragment is legitimately gone because we already stripped it. Wiping storage
 * on "no fragment present" would therefore delete the invite precisely when the
 * user came back to use it. Only an explicit new invite overwrites.
 */
export function captureInviteKeyFromUrl() {
    const rawHash = window.location.hash;
    if (!rawHash || rawHash.length < 2) {
        return null;
    }

    const params = new URLSearchParams(rawHash.slice(1));
    if (!params.has('invite')) {
        // A fragment that is not ours (e.g. a deep-link anchor). Leave it alone.
        return null;
    }

    const inviteKey = (params.get('invite') || '').trim().toUpperCase();

    // Remove it from the address bar BEFORE any validation branch, so a
    // malformed key is scrubbed just as aggressively as a well-formed one. A
    // typo'd key is still a near-miss of a live key and does not belong in the
    // URL bar, screenshots, or shoulder-surfing range.
    stripFragmentFromVisibleUrl();

    if (!INVITE_KEY_PATTERN.test(inviteKey)) {
        // Loud, but recoverable: the link carried something that cannot be a
        // beta key at all — blank, longer than `key_code`'s max_length=100, or
        // containing characters no key uses (typically a truncated or
        // URL-mangled link). The user lands on the key form rather than the SPA
        // dying.
        //
        // NOTE: this branch NO LONGER fires merely for "wrong prefix". Key TYPE
        // is decided by the backend's `key_type` after validation — see the
        // comment on INVITE_KEY_PATTERN.
        //
        // Only the LENGTH is reported. The value is NOT logged: a mangled
        // near-miss of a live key does not belong in console output, which leaks
        // into screen shares, bug screenshots, and session-replay capture.
        console.error(
            'inviteKey: invite link rejected — the invite value is not a ' +
            `possible beta key (length ${inviteKey.length}). Ignoring it and ` +
            'asking for manual entry.'
        );
        sessionStorage.removeItem(INVITE_STORAGE_KEY);
        sessionStorage.setItem(INVITE_ERROR_KEY, 'malformed');
        return null;
    }

    sessionStorage.setItem(INVITE_STORAGE_KEY, inviteKey);
    sessionStorage.removeItem(INVITE_ERROR_KEY);
    return inviteKey;
}

/** The pending invite for this tab, or `null` when the user arrived normally. */
export function getStoredInviteKey() {
    return sessionStorage.getItem(INVITE_STORAGE_KEY);
}

/**
 * Replace the pending invite with a key the user corrected by hand.
 *
 * Without this, a user who fixes a bad invite key in the field holds the good
 * value in React state ONLY — a refresh mid-signup silently restores the
 * original broken key from storage and undoes their correction. Persisting the
 * replacement keeps storage and the field in agreement.
 *
 * Clears the malformed-link marker: whatever was wrong with the original link,
 * the user has now moved past it.
 */
export function setStoredInviteKey(inviteKey) {
    if (!inviteKey) {
        throw new Error('setStoredInviteKey requires a non-empty key');
    }
    sessionStorage.setItem(INVITE_STORAGE_KEY, inviteKey.trim().toUpperCase());
    sessionStorage.removeItem(INVITE_ERROR_KEY);
}

/** True when this tab opened an invite link we could not parse. */
export function hasInviteLinkError() {
    return sessionStorage.getItem(INVITE_ERROR_KEY) === 'malformed';
}

/**
 * True when this tab has ANY pending invite business — a usable key, or a
 * rejected link that still owes the user an explanation.
 *
 * Routing must use THIS, not `getStoredInviteKey()`. A malformed or non-shared
 * link stores no key, so a key-only check sent those visitors to the plain login
 * screen and the "that link didn't look right" banner could never render —
 * the failure was silent, which is precisely what it must not be.
 */
export function hasPendingInvite() {
    return Boolean(getStoredInviteKey()) || hasInviteLinkError();
}

/**
 * Drop the invite once it can no longer be acted on.
 *
 * MUST be called after the key is actually consumed (successful registration),
 * otherwise the stale key keeps prefilling the field for the rest of the tab's
 * life and the next person to sign up on a shared machine inherits it.
 */
export function clearStoredInvite() {
    sessionStorage.removeItem(INVITE_STORAGE_KEY);
    sessionStorage.removeItem(INVITE_ERROR_KEY);
}
